// ----------------------------------------------------------------
import CheckOutlined from "@/assets/icons/CheckOutlined";
import {
  Copy,
  Users,
  FolderInput,
  Pencil,
  Trash,
  Search,
  CheckSquare,
  MessageSquare,
  Inbox,
  Bot,
  Files,
  CheckCheck,
} from "lucide-react";
// ----------------------------------------------------------------

/**
 * Default tenant id for the `X-Tenant-Id` header on API requests (excludes login).
 */
export const DEFAULT_TENANT_ID = "1";

/**
 * The SIDEBAR_ITEMS constant is an array of objects that represent the items in the sidebar.
 * It contains the label, icon, activeIcon, to, and tags for each item.
 */
export const SIDEBAR_ITEMS = [
  {
    label: "Groups",
    icon: Users,
    activeIcon: Users,
    to: "/",
    tags: ["group", "item"],
  },
  {
    label: "Search",
    icon: Search,
    activeIcon: Search,
    to: "/search",
    tags: [],
  },
  {
    label: "Inbox",
    icon: Inbox,
    activeIcon: Inbox,
    to: "/notifications",
    tags: [],
  },
  {
    label: "Tasks",
    icon: CheckOutlined,
    activeIcon: CheckOutlined,
    to: "/tasks",
    tags: [],
  },
  {
    label: "KI Assistent",
    icon: Bot,
    activeIcon: Bot,
    to: "openAiChat",
    shouldFillBackground: false,
    tags: [],
  },
] as const;

/**
 * The TREE_DATA constant is an array of objects that represent the tree data.
 * It contains the id, name, and children for each item.
 */
export const TREE_DATA = [
  {
    id: "1",
    name: "Folder 1",
    children: [
      {
        id: "1.1",
        name: "Folder 1.1",
        children: [
          {
            id: "1.1.1",
            name: "Folder 1.1.1",
          },
        ],
      },
      {
        id: "1.2",
        name: "Folder 1.2",
      },
    ],
  },
  {
    id: "2",
    name: "Folder 2",
    children: [
      {
        id: "2.1",
        name: "Folder 2.1",
      },
      {
        id: "2.2",
        name: "Folder 2.2",
      },
    ],
  },
];

/**
 * The CONTEXT_MENU_ITEMS constant is an array of objects that represent the context menu items.
 * It contains the label, icon, onClick, action, and disabled for each item.
 */
export const CONTEXT_MENU_ITEMS = [
  {
    label: "Select",
    icon: CheckSquare,
    onClick: () => null as unknown as void,
    action: "select",
    disabled: false,
  },
  {
    label: "Move",
    icon: FolderInput,
    onClick: () => null as unknown as void,
    action: "move",
    disabled: false,
  },
  {
    label: "Duplicate",
    icon: Files,
    onClick: () => null as unknown as void,
    action: "duplicate",
    disabled: false,
  },
  {
    label: "Edit",
    icon: Pencil,
    onClick: () => null as unknown as void,
    action: "edit",
    disabled: false,
  },
  {
    label: "Comment",
    icon: MessageSquare,
    onClick: () => null as unknown as void,
    action: "comment",
    disabled: false,
  },
  {
    label: "Mark as read",
    icon: CheckCheck,
    onClick: () => null as unknown as void,
    action: "mark-read",
    disabled: false,
  },
  {
    label: "Copy selection to clipboard",
    icon: Copy,
    onClick: () => null as unknown as void,
    action: "copy",
    disabled: false,
  },
  {
    label: "Delete",
    icon: Trash,
    onClick: () => null as unknown as void,
    action: "delete",
    disabled: false,
  },
];

/**
 * The TASK_STATUS constant is an object that represents the status of a task.
 * It contains the TODO, IN_PROGRESS, DONE, and CANCELLED statuses.
 */
export const TASK_STATUS = {
  TODO: "todo",
  IN_PROGRESS: "in_progress",
  DONE: "done",
  CANCELLED: "cancelled",
} as const;

/** Backend home-folder id for group-scoped member chat (`list-item-comment` with `groupId`). */
export const GROUP_LEVEL_LIST_ITEM_ID = "-1";

/** Number of messages fetched per page from the group-chat API. */
export const CHAT_PAGE_SIZE = 50;

/** Maximum page size enforced by the notifications API. */
export const NOTIFICATION_MAX_PAGE_SIZE = 100;

/** Default inbox list page size. */
export const NOTIFICATION_DEFAULT_PAGE_SIZE = 50;

/** Duration (ms) of the chat message highlight-pulse animation. Must match `.highlight-pulse` in styles.css. */
export const CHAT_MESSAGE_HIGHLIGHT_MS = 2500;

export const LOGOUT_IN_PROGRESS_FLAG = "__APP_LOGOUT_IN_PROGRESS__" as const;

/** Set while redirecting to login after invalid token to dedupe parallel 401 handling. */
export const AUTH_SESSION_REDIRECT_PENDING_FLAG =
  "__AUTH_SESSION_REDIRECT_PENDING__" as const;
